// Pure helpers for the generation (multimodal) playground. No side effects
// besides readFileAsDataURI, which wraps the browser FileReader API.

import type { CatalogEntry } from '../services/inferenceApi';

export interface MediaItem {
  contentUrl: string;
  contentType?: string;
  width?: number;
  height?: number;
  fileName?: string;
  // Absolute, signed, public link for this item (sibling `url` field on the
  // result object) - shareable without an Authorization header, expires
  // after a server-side TTL. Only captured when it's an absolute http(s)
  // string; any other shape (relative path, non-string, other scheme) is
  // defensively treated as absent rather than surfaced as a broken link.
  artifactUrl?: string;
}

export interface PanelConfig {
  promptLabel: string;
  promptField: 'prompt' | 'text';
  promptPlaceholder: string;
  extraInput?: 'image_url' | 'audio_url';
  submitLabel: string;
  // When true, the panel renders no prompt textarea (e.g. transcription,
  // whose primary input is the audio_url extraInput instead).
  noPrompt?: true;
}

const NOT_READY_KEYS = new Set(['id', 'status', 'queue_position']);

// True iff json is an in-flight generation status/result body: an object
// whose keys are a subset of {id, status, queue_position} and whose status
// is a string other than "completed". Anything carrying extra keys (e.g.
// content_url) is treated as a finished result, not a not-ready poll body.
export function isNotReadyResult(json: unknown): boolean {
  if (typeof json !== 'object' || json === null || Array.isArray(json)) return false;
  const keys = Object.keys(json as Record<string, unknown>);
  if (!keys.every((k) => NOT_READY_KEYS.has(k))) return false;
  const status = (json as Record<string, unknown>).status;
  return typeof status === 'string' && status !== 'completed';
}

// Recursively walks result, collecting every object that carries a string
// content_url field, in traversal order (object key order, then array
// index order).
export function extractMediaItems(result: unknown): MediaItem[] {
  const items: MediaItem[] = [];

  const visit = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (typeof node !== 'object' || node === null) return;
    const obj = node as Record<string, unknown>;
    if (typeof obj.content_url === 'string') {
      const item: MediaItem = { contentUrl: obj.content_url };
      if (typeof obj.content_type === 'string') item.contentType = obj.content_type;
      if (typeof obj.width === 'number') item.width = obj.width;
      if (typeof obj.height === 'number') item.height = obj.height;
      if (typeof obj.file_name === 'string') item.fileName = obj.file_name;
      if (typeof obj.url === 'string' && /^https?:\/\//i.test(obj.url)) item.artifactUrl = obj.url;
      items.push(item);
    }
    Object.values(obj).forEach(visit);
  };

  visit(result);
  return items;
}

const TEXT_OUTPUT_KEYS = ['text', 'transcript', 'transcription', 'output'] as const;

// First string value among top-level keys text, transcript, transcription,
// output (in that order); else null.
export function extractTextOutput(result: unknown): string | null {
  if (typeof result !== 'object' || result === null || Array.isArray(result)) return null;
  const obj = result as Record<string, unknown>;
  for (const key of TEXT_OUTPUT_KEYS) {
    const value = obj[key];
    if (typeof value === 'string') return value;
  }
  return null;
}

// Some providers (e.g. fal) return generated media with a generic/empty
// content_type (notably "application/octet-stream") but a sibling file_name
// carrying the real extension (e.g. "output.mp4"). Those two content_type
// values are treated as non-decisive so kind detection can fall through to
// the file_name/URL extension instead of landing on 'file'.
const NON_DECISIVE_CONTENT_TYPES = new Set(['application/octet-stream', '']);

const EXTENSION_KIND: Record<string, 'image' | 'video' | 'audio'> = {
  mp4: 'video', mov: 'video', webm: 'video', m4v: 'video',
  png: 'image', jpg: 'image', jpeg: 'image', webp: 'image', gif: 'image',
  mp3: 'audio', wav: 'audio', m4a: 'audio', ogg: 'audio', flac: 'audio',
};

// Lowercased file extension (no dot) from a filename or URL path, ignoring
// any query string/fragment; null if there isn't one.
function extensionOf(pathOrUrl: string): string | null {
  const withoutQuery = pathOrUrl.split(/[?#]/)[0];
  const match = /\.([a-zA-Z0-9]+)$/.exec(withoutQuery);
  return match ? match[1].toLowerCase() : null;
}

function kindFromExtension(pathOrUrl: string | undefined): 'image' | 'video' | 'audio' | null {
  if (!pathOrUrl) return null;
  const ext = extensionOf(pathOrUrl);
  return ext ? EXTENSION_KIND[ext] ?? null : null;
}

// Determines the media kind for rendering. Precedence:
//   1. contentType prefix (image/video/audio), when contentType is present
//      and "decisive" (not application/octet-stream or empty - see
//      NON_DECISIVE_CONTENT_TYPES). A decisive-but-unmatched contentType
//      (e.g. application/zip) resolves straight to 'file'.
//   2. file_name extension (fal, notably, returns generated videos as
//      application/octet-stream with a sibling file_name like "output.mp4").
//   3. contentUrl path extension.
//   4. model category fallback (unchanged).
//   5. 'file'.
export function mediaKind(item: MediaItem, category: string): 'image' | 'video' | 'audio' | 'file' {
  if (item.contentType && !NON_DECISIVE_CONTENT_TYPES.has(item.contentType)) {
    if (item.contentType.startsWith('image/')) return 'image';
    if (item.contentType.startsWith('video/')) return 'video';
    if (item.contentType.startsWith('audio/')) return 'audio';
    return 'file';
  }
  const fromFileName = kindFromExtension(item.fileName);
  if (fromFileName) return fromFileName;
  const fromUrl = kindFromExtension(item.contentUrl);
  if (fromUrl) return fromUrl;
  if (category === 'image') return 'image';
  if (category === 'video') return 'video';
  if (category === 'speech' || category === 'audio') return 'audio';
  return 'file';
}

const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'video/mp4': 'mp4',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
};

// Extension for a synthesized filename: the file_name extension (if any)
// wins, then the contentType map, else 'bin'.
function synthesizedExtension(item: MediaItem): string {
  const fromFileName = item.fileName ? extensionOf(item.fileName) : null;
  if (fromFileName) return fromFileName;
  if (item.contentType) return EXTENSION_BY_CONTENT_TYPE[item.contentType] ?? 'bin';
  return 'bin';
}

// Strips any path (both / and \ separators) from fileName and trims
// whitespace, returning the sanitized basename, or null if fileName is
// absent or sanitizes to nothing usable.
function sanitizedBasename(fileName?: string): string | null {
  if (!fileName) return null;
  const base = fileName.split(/[\\/]/).pop() ?? '';
  const trimmed = base.trim();
  return trimmed || null;
}

// Download filename for a resolved media item: prefers the sanitized
// basename of the provider-supplied file_name (e.g. fal's "output.mp4")
// over the synthesized "<slug>-<runPrefix>-<index>.<ext>" fallback, so
// downloads carry a meaningful name/extension even when content_type is
// generic (application/octet-stream) or missing. runId is truncated to its
// first 8 characters in the synthesized form, matching prior behavior.
export function resolveMediaFilename(item: MediaItem, slug: string, runId: string, index: number): string {
  const sanitized = sanitizedBasename(item.fileName);
  if (sanitized) return sanitized;
  const runPrefix = runId.slice(0, 8);
  return `${slug}-${runPrefix}-${index}.${synthesizedExtension(item)}`;
}

// Reads a File as a data: URI, rejecting up front if it exceeds maxBytes.
export function readFileAsDataURI(file: File, maxBytes: number): Promise<string> {
  if (file.size > maxBytes) {
    return Promise.reject(new Error('File too large (max 5 MB)'));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// Extracts a human-readable message from a parsed error-response body.
// The gateway returns nested OpenAI-style envelopes
// ({"error": {"message","type","code"}}), but some paths (and error
// fallbacks) may still emit a bare string under "error", so both shapes
// are handled explicitly rather than assuming one.
export function extractApiErrorMessage(json: unknown): string {
  if (typeof json === 'object' && json !== null) {
    const err = (json as Record<string, unknown>).error;
    if (typeof err === 'object' && err !== null) {
      const message = (err as Record<string, unknown>).message;
      if (typeof message === 'string') return message;
    }
    if (typeof err === 'string') return err;
  }
  return 'Request failed';
}

// Shallow-merges advancedJSON (a user-typed JSON object string) over base.
// Empty/whitespace-only input is a no-op; anything that isn't a JSON object
// throws.
export function mergeAdvancedParams(base: Record<string, unknown>, advancedJSON: string): Record<string, unknown> {
  if (advancedJSON.trim() === '') return base;
  let parsed: unknown;
  try {
    parsed = JSON.parse(advancedJSON);
  } catch {
    throw new Error('Advanced parameters must be a JSON object');
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('Advanced parameters must be a JSON object');
  }
  return { ...base, ...(parsed as Record<string, unknown>) };
}

// Mirrors the server's metering quantity rules exactly
// (podstack-core-backend services/inference-service internal/handlers/metering.go
// ExtractMetering), so the playground's live estimate lines up with what the
// backend will actually charge:
//   - per_video_second: params.duration, if present and > 0, capped at 3600;
//     else 1. Not rounded - a fractional duration stays fractional.
//   - per_100_frames: ceil(params.num_frames / 100), if present and > 0,
//     capped at 100; else 1.
//   - per_audio_minute: ceil(params.duration / 60), if present and > 0,
//     capped at 600; else 1.
//   - per_request, per_image (result-counted server-side; estimate: 1), and
//     any other/unknown billing unit: always 1.
export function estimateQuantity(
  billingUnit: string,
  params: { duration?: number; num_frames?: number }
): number {
  switch (billingUnit) {
    case 'per_video_second': {
      const d = params.duration;
      return typeof d === 'number' && d > 0 ? Math.min(d, 3600) : 1;
    }
    case 'per_100_frames': {
      const n = params.num_frames;
      return typeof n === 'number' && n > 0 ? Math.min(Math.ceil(n / 100), 100) : 1;
    }
    case 'per_audio_minute': {
      const d = params.duration;
      return typeof d === 'number' && d > 0 ? Math.min(Math.ceil(d / 60), 600) : 1;
    }
    default:
      return 1;
  }
}

// Estimates the cost of a generation request: per-unit price × quantity.
// Tier selection mirrors the server's models.SelectTier semantics: an exact,
// case-insensitive key match on `resolution` wins, otherwise (miss or empty
// resolution) falls back to the default tier. Returns null when pricing
// isn't available, or (defensively) when tiered with no default tier
// resolvable. For untiered entries, the per-unit price is
// input_price_per_1m - but only for non-token billing units (per_1m_tokens
// pricing isn't quantity-per-request in the sense this estimate models).
export function estimateCost(entry: CatalogEntry, resolution: string, quantity: number): number | null {
  if (!entry.pricing_available) return null;
  if (entry.price_tiers.length > 0) {
    const match = resolution
      ? entry.price_tiers.find((t) => t.key.toLowerCase() === resolution.toLowerCase())
      : undefined;
    const tier = match ?? entry.price_tiers.find((t) => t.default);
    if (!tier) return null;
    return tier.unit_price * quantity;
  }
  if (entry.billing_unit === 'per_1m_tokens') return null;
  return entry.input_price_per_1m * quantity;
}

export const PANEL_CONFIG: Record<string, PanelConfig> = {
  image: {
    promptLabel: 'Prompt',
    promptField: 'prompt',
    promptPlaceholder: 'Describe the image to generate…',
    submitLabel: 'Generate',
  },
  video: {
    promptLabel: 'Prompt',
    promptField: 'prompt',
    promptPlaceholder: 'Describe the video to generate…',
    extraInput: 'image_url',
    submitLabel: 'Generate',
  },
  speech: {
    promptLabel: 'Text',
    promptField: 'text',
    promptPlaceholder: 'Text to speak…',
    submitLabel: 'Generate speech',
  },
  transcription: {
    promptLabel: 'Audio URL',
    promptField: 'prompt',
    promptPlaceholder: 'Audio URL to transcribe…',
    extraInput: 'audio_url',
    submitLabel: 'Transcribe',
    noPrompt: true,
  },
  audio: {
    promptLabel: 'Prompt',
    promptField: 'prompt',
    promptPlaceholder: 'Describe the audio/music to generate…',
    submitLabel: 'Generate',
  },
};
